import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  IMPORT_MAX_CONTACTS,
  importContactSchema,
  type ImportContactInput,
  type ImportResult,
} from '@ehr/shared';
import { api } from '../lib/api.js';

export type ImportExportKind = 'contacts' | 'activity' | 'followups' | 'category' | 'backup';

/**
 * Import / Export Data (V34 parity) — the 3-dot menu's "Import / Export" entry.
 * Download contacts / activity / follow-ups as CSV, or category / full backups as
 * JSON, scoped to the current category or all. Import brings contacts in from a
 * CSV. Generation is client-side from the core read APIs.
 */

type ExportKind = ImportExportKind;
const CATS = ['client', 'referral_source', 'staff'] as const;
const catPlural = (c: string) =>
  c === 'referral_source' ? 'Referral Sources' : c === 'staff' ? 'Staff' : 'Clients';

const CARDS: { key: ExportKind; fmt: 'CSV' | 'JSON'; title: string; desc: string }[] = [
  {
    key: 'contacts',
    fmt: 'CSV',
    title: 'Contacts',
    desc: 'One row per contact with all chosen fields. Opens in Excel, Sheets, Numbers.',
  },
  {
    key: 'activity',
    fmt: 'CSV',
    title: 'Activity log',
    desc: 'One row per note, call, email, or meeting — with contact and date.',
  },
  {
    key: 'followups',
    fmt: 'CSV',
    title: 'Follow-ups',
    desc: 'Just contacts with scheduled follow-up dates. Sorted by date.',
  },
  {
    key: 'category',
    fmt: 'JSON',
    title: 'Category export',
    desc: 'Full structured data for one category — fields, notes, schema. Importable.',
  },
  {
    key: 'backup',
    fmt: 'JSON',
    title: 'Full backup',
    desc: 'Everything: all categories, templates, schemas, users, settings.',
  },
];

type ContactRow = {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  mrn?: string | null;
  category: string;
  statusId?: string | null;
  stageId?: string | null;
  createdAt?: string | null;
};

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const toCsv = (headers: string[], rows: unknown[][]) =>
  [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');

/**
 * The Contacts CSV. ehr#265 - it carries each contact's Credify ID (as "Client ID", which
 * the import reads as the match key) and MRN: without them a re-imported export matched
 * only on email, so every client without an email was copied again.
 */
export const contactsCsv = (rows: ContactRow[]) =>
  toCsv(
    ['Client ID', 'First name', 'Last name', 'Email', 'Phone', 'MRN', 'Category', 'Created'],
    rows.map((c) => [
      c.id,
      c.firstName,
      c.lastName,
      c.email,
      c.phone,
      c.mrn,
      c.category,
      c.createdAt,
    ]),
  );

function saveBlob(name: string, mime: string, content: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const stamp = () => new Date().toISOString().slice(0, 10);

/**
 * RFC-4180-ish CSV parser: handles quoted fields with embedded commas, quotes
 * ("" escape) and newlines, plus \r\n / \r / \n line breaks. The old importer
 * split on commas and newlines naively, which corrupted any address or note
 * containing either.
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else inQuotes = false;
      } else cell += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

/**
 * Column aliases → normalized field. This is the seam a specific source system's
 * export plugs into: extend the alias lists without touching the import call.
 * Matching (mapColumns): a header EQUAL to an alias wins first (so the Credify
 * template's columns always land right), then the first unclaimed header that
 * CONTAINS an alias, skipping any `exclude` words. Order matters for the substring
 * pass — specific fields (Payor phone, Member ID) sit above generic ones (Phone).
 */
const FIELD_ALIASES: { field: string; aliases: string[]; exclude?: string[] }[] = [
  {
    field: 'externalId',
    aliases: [
      'external id',
      'externalid',
      'legacy id',
      'source id',
      'record id',
      'mrn id',
      'patient id',
      'client id',
    ],
  },
  // #31: Status, Referral source and the primary Insurance / Payor.
  {
    field: 'status',
    aliases: ['status', 'client status'],
    exclude: ['marital', 'insurance', 'claim', 'employ', 'eligib', 'appointment', 'appt', 'visit'],
  },
  { field: 'referralSource', aliases: ['referral source', 'referred by', 'referral'] },
  { field: 'insurancePhone', aliases: ['insurance phone', 'payor phone', 'payer phone'] },
  {
    field: 'insuranceMemberId',
    aliases: ['insurance member id', 'member id', 'subscriber id', 'insurance id', 'policy'],
  },
  { field: 'insuranceGroup', aliases: ['insurance group', 'group number', 'group #', 'group'] },
  {
    field: 'insurancePayer',
    aliases: [
      'insurance payor',
      'insurance payer',
      'insurance company',
      'insurance',
      'payor',
      'payer',
      'carrier',
    ],
  },
  // #31: upcoming appointments (one row per appointment, client columns repeated).
  {
    field: 'apptDate',
    aliases: ['appointment date', 'appt date', 'visit date', 'next appointment'],
  },
  { field: 'apptTime', aliases: ['appointment time', 'appt time', 'visit time', 'start time'] },
  { field: 'apptDuration', aliases: ['appointment duration', 'appt duration', 'duration'] },
  { field: 'apptType', aliases: ['appointment type', 'appt type', 'visit type', 'service type'] },
  {
    field: 'apptProvider',
    aliases: ['appointment provider', 'appt provider', 'rendering provider', 'scheduled with'],
  },
  { field: 'apptLocation', aliases: ['appointment location', 'appt location', 'location'] },
  { field: 'apptTelehealth', aliases: ['appointment telehealth', 'telehealth', 'virtual'] },
  { field: 'firstName', aliases: ['first name', 'firstname', 'first', 'given'] },
  { field: 'lastName', aliases: ['last name', 'lastname', 'last', 'surname', 'family'] },
  { field: 'email', aliases: ['email', 'e-mail'] },
  { field: 'phone', aliases: ['phone', 'mobile', 'cell', 'telephone'] },
  { field: 'dob', aliases: ['dob', 'birth', 'date of birth'] },
  { field: 'sex', aliases: ['sex', 'gender'] },
  { field: 'mrn', aliases: ['mrn', 'medical record'] },
  { field: 'address1', aliases: ['address 1', 'address1', 'street', 'address'] },
  { field: 'address2', aliases: ['address 2', 'address2', 'apt', 'unit', 'suite'] },
  { field: 'city', aliases: ['city', 'town'] },
  { field: 'state', aliases: ['state', 'province', 'region'] },
  { field: 'zip', aliases: ['zip', 'postal', 'postcode'] },
  { field: 'note', aliases: ['note', 'comment', 'progress', 'chart note', 'history'] },
  { field: 'noteDate', aliases: ['note date', 'entry date', 'service date'] },
  {
    field: 'noteAuthor',
    aliases: ['note author', 'author', 'clinician', 'provider', 'staff', 'therapist'],
  },
];

export function mapColumns(header: string[]): Record<string, number> {
  const lower = header.map((h) => h.trim().toLowerCase());
  const idx: Record<string, number> = {};
  const claimed = new Set<number>();
  const claim = (field: string, at: number) => {
    idx[field] = at;
    claimed.add(at);
  };
  // Pass 1: exact header names (the import template).
  for (const { field, aliases } of FIELD_ALIASES) {
    const at = lower.findIndex((h, i) => !claimed.has(i) && aliases.includes(h));
    if (at >= 0) claim(field, at);
  }
  // Pass 2: substring match on what's left — a taken column no longer blocks the
  // field from its next matching column.
  for (const { field, aliases, exclude } of FIELD_ALIASES) {
    if (field in idx) continue;
    const at = lower.findIndex(
      (h, i) =>
        !claimed.has(i) &&
        aliases.some((a) => h.includes(a)) &&
        !(exclude ?? []).some((x) => h.includes(x)),
    );
    if (at >= 0) claim(field, at);
  }
  return idx;
}

/**
 * ehr#31 - every column that holds a note, left to right. Files from other systems put a
 * client's notes side by side ("Progress Note 1", "Progress Note 2", …) instead of one row
 * per note; mapColumns gives a field ONE column, so only the first was imported. A note
 * column is one the matched note column, or any other unclaimed column named like a note
 * (note / notes / progress / chart note / comment), never a Note Date / Note Author column.
 */
export function noteColumns(header: string[], col: Record<string, number>): number[] {
  const claimed = new Set(
    Object.entries(col)
      .filter(([f]) => f !== 'note')
      .map(([, i]) => i),
  );
  const isNote = (h: string) =>
    /\bnotes?\b|\bprogress\b|\bchart note|\bcomments?\b/.test(h) && !/\bdate\b|\bauthor\b/.test(h);
  return header
    .map((h, i) => ({ h: h.trim().toLowerCase(), i }))
    .filter(({ h, i }) => i === col.note || (!claimed.has(i) && isNote(h)))
    .map(({ i }) => i);
}

/** ehr#31 - a note that starts with its own date ("08/11/2026: Introduced …") → that date. */
export function leadingNoteDate(body: string): Date | undefined {
  const m = /^\s*(\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}|\d{4}-\d{1,2}-\d{1,2})(?=\s|:|-|–|—|$)/.exec(
    body,
  );
  const p = m ? parseDateCell(m[1]!) : null;
  return p ? new Date(p.y, p.m - 1, p.d, 12) : undefined;
}

/** A week from today as MM/DD/YYYY, so the template's example appointment is upcoming. */
function exampleApptDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

/**
 * ehr#103 — the Credify import template: every column the importer reads, with one
 * made-up example row. Header names match FIELD_ALIASES exactly (pass 1 above).
 */
export const TEMPLATE_HEADERS = [
  'External ID',
  'First Name',
  'Last Name',
  'Email',
  'Phone',
  'Date of Birth',
  'Sex',
  'MRN',
  'Address 1',
  'Address 2',
  'City',
  'State',
  'ZIP',
  'Status',
  'Referral Source',
  'Insurance Payor',
  'Insurance Member ID',
  'Insurance Group',
  'Insurance Phone',
  'Note',
  'Note Date',
  'Note Author',
  'Appointment Date',
  'Appointment Time',
  'Appointment Duration',
  'Appointment Type',
  'Appointment Provider',
  'Appointment Location',
  'Appointment Telehealth',
];
export const TEMPLATE_EXAMPLE = [
  'OLD-1001',
  'Jordan',
  'Example',
  'jordan.example@example.com',
  '(555) 010-0100',
  '03/12/1985',
  'Female',
  'MRN-1001',
  '100 Main St',
  'Apt 2',
  'Springfield',
  'CA',
  '90001',
  'Active Client',
  'Dr. Referral Example',
  'Aetna',
  'W123456789',
  'GRP-42',
  '(800) 555-0199',
  'Intake at prior clinic. One row per note; repeat the client columns.',
  '02/10/2024',
  'Dr. Prior',
  exampleApptDate(),
  '2:30 PM',
  '50',
  'Individual',
  'Clinician full name or email (an EHR user)',
  'Main Office',
  'No',
];

const trimOrUndef = (v: string | undefined) => {
  const s = (v ?? '').trim();
  return s === '' ? undefined : s;
};

/** #31 — a date cell (ISO `YYYY-MM-DD` or US `M/D/YYYY`, `-`/`.` too) → parts, or null. */
export function parseDateCell(s: string): { y: number; m: number; d: number } | null {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ].*)?$/.exec(s.trim());
  const us = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(s.trim());
  let y: number, m: number, d: number;
  if (iso) [y, m, d] = [Number(iso[1]), Number(iso[2]), Number(iso[3])];
  else if (us) {
    [m, d, y] = [Number(us[1]), Number(us[2]), Number(us[3])];
    if (us[3]!.length === 2) y += 2000;
  } else return null;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
    ? { y, m, d }
    : null;
}

/** #31 — a time cell (`14:30`, `2:30 PM`, `2pm`, `1430`) → 24h parts, or null. */
export function parseTimeCell(s: string): { h: number; min: number } | null {
  const t = s.trim().toLowerCase().replace(/\./g, '');
  const m = /^(\d{1,2})(?::?(\d{2}))?\s*(am|pm|a|p)?$/.exec(t);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const ap = m[3]?.[0];
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === 'p' && h !== 12) h += 12;
    if (ap === 'a' && h === 12) h = 0;
  }
  return h <= 23 && min <= 59 ? { h, min } : null;
}

const YES = /^(y|yes|true|1|telehealth|virtual|video)$/i;

/** Tallies from buildRecords the API never sees (rows skipped in the browser). */
export interface BuildStats {
  apptPast: number;
  apptUnreadable: number;
}

/**
 * Build normalized import records from parsed CSV rows. Rows sharing the same
 * contact (matched by externalId, else email) merge into one record and collect
 * their note cells, so N rows for one client become 1 contact + N notes.
 */
export function buildRecords(
  rows: string[][],
  category: string,
  stats: BuildStats = { apptPast: 0, apptUnreadable: 0 },
  now: Date = new Date(),
): ImportContactInput[] {
  // #31: only upcoming appointments are imported — anything before today (local) is
  // skipped here, so old visits never reach the Calendar or RCM's billable list.
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (rows.length < 2) return [];
  const col = mapColumns(rows[0]!);
  const at = (cells: string[], field: string) =>
    col[field] !== undefined ? trimOrUndef(cells[col[field]!]) : undefined;
  const noteCols = noteColumns(rows[0]!, col); // ehr#31: all of a row's note columns

  const byKey = new Map<string, ImportContactInput>();
  const order: string[] = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r]!;
    const firstName = at(cells, 'firstName') ?? cells[0]?.trim();
    const lastName = at(cells, 'lastName') ?? cells[1]?.trim();
    if (!firstName || !lastName) continue;
    const externalId = at(cells, 'externalId');
    const email = at(cells, 'email');
    const key = externalId ? `x:${externalId}` : email ? `e:${email.toLowerCase()}` : `r:${r}`;

    let rec = byKey.get(key);
    if (!rec) {
      rec = {
        category: category as ImportContactInput['category'],
        firstName,
        lastName,
        externalId,
        email,
        phone: at(cells, 'phone'),
        dob: at(cells, 'dob'),
        sex: at(cells, 'sex'),
        mrn: at(cells, 'mrn'),
        address1: at(cells, 'address1'),
        address2: at(cells, 'address2'),
        city: at(cells, 'city'),
        state: at(cells, 'state'),
        zip: at(cells, 'zip'),
        status: at(cells, 'status'),
        referralSource: at(cells, 'referralSource'),
        insurancePayer: at(cells, 'insurancePayer'),
        insuranceMemberId: at(cells, 'insuranceMemberId'),
        insuranceGroup: at(cells, 'insuranceGroup'),
        insurancePhone: at(cells, 'insurancePhone'),
        notes: [],
        appointments: [],
      };
      byKey.set(key, rec);
      order.push(key);
    }
    // ehr#31: every note column on the row (Progress Note 1, 2, 3 …), left to right. The
    // Note Date column dates the main note; any note that starts with a date uses that.
    for (const ci of noteCols) {
      const noteBody = trimOrUndef(cells[ci]);
      if (!noteBody) continue;
      const dateStr = ci === col.note ? at(cells, 'noteDate') : undefined;
      const parsed = dateStr ? new Date(dateStr) : null;
      rec.notes!.push({
        body: noteBody,
        type: 'note',
        authorLabel: at(cells, 'noteAuthor'),
        date: parsed && !isNaN(parsed.getTime()) ? parsed : leadingNoteDate(noteBody),
      });
    }
    // #31: an appointment on this row — date + time read in the user's local zone,
    // like the drawer's scheduler; the API receives ISO instants.
    const apptDate = at(cells, 'apptDate');
    if (apptDate) {
      const d = parseDateCell(apptDate);
      const t = parseTimeCell(at(cells, 'apptTime') ?? '');
      if (!d || !t) {
        stats.apptUnreadable++;
      } else {
        const start = new Date(d.y, d.m - 1, d.d, t.h, t.min);
        if (start < todayStart) {
          stats.apptPast++;
        } else {
          const mins = Number(at(cells, 'apptDuration'));
          const dur = Number.isFinite(mins) && mins > 0 && mins <= 24 * 60 ? mins : 50;
          rec.appointments!.push({
            startsAt: start,
            endsAt: new Date(start.getTime() + dur * 60_000),
            type: at(cells, 'apptType'),
            provider: at(cells, 'apptProvider'),
            location: at(cells, 'apptLocation'),
            telehealth: YES.test(at(cells, 'apptTelehealth') ?? ''),
          });
        }
      }
    }
  }
  return order.map((k) => {
    const rec = byKey.get(k)!;
    if (rec.notes && rec.notes.length === 0) delete rec.notes;
    if (rec.appointments && rec.appointments.length === 0) delete rec.appointments;
    return rec;
  });
}

/** ehr#31 - an Excel workbook (a zip) or other binary file picked instead of a CSV. */
export function looksBinary(text: string): boolean {
  return text.startsWith('PK') || text.slice(0, 2000).includes('\u0000');
}

const IMPORT_FIELD_LABEL: Record<string, string> = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  notes: 'A note',
  appointments: 'An appointment',
};

/**
 * ehr#31 - check every record the way the API will, so one bad row is skipped (and named)
 * instead of the whole file failing with "Invalid request body".
 */
export function splitValid(records: ImportContactInput[]): {
  ok: ImportContactInput[];
  bad: string[];
} {
  const ok: ImportContactInput[] = [];
  const bad: string[] = [];
  for (const rec of records) {
    const r = importContactSchema.safeParse(JSON.parse(JSON.stringify(rec)));
    if (r.success) {
      ok.push(rec);
      continue;
    }
    const issue = r.error.issues[0]!;
    const key = String(issue.path[0] ?? '');
    const label = IMPORT_FIELD_LABEL[key] ?? key;
    const why =
      issue.code === 'too_big' && 'maximum' in issue
        ? `is too long (${String(issue.maximum)} at most)`
        : issue.message.toLowerCase();
    const who = `${rec.firstName ?? ''} ${rec.lastName ?? ''}`.trim().slice(0, 40) || 'A row';
    bad.push(`${who}: ${label ? `${label} ` : ''}${why}`);
  }
  return { ok, bad };
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export function ImportExportModal({
  category,
  onClose,
  initialKind,
  autoImport,
}: {
  category: string;
  onClose: () => void;
  /** Preselect an export card (e.g. 'backup' from the "Download JSON backup" button). */
  initialKind?: ExportKind;
  /** Open the CSV file picker immediately (the "Import from file" entry point). */
  autoImport?: boolean;
}) {
  const [kind, setKind] = useState<ExportKind>(initialKind ?? 'contacts');
  const [scope, setScope] = useState<'category' | 'all'>('category');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState(true); // dry-run by default — safer
  const fileRef = useRef<HTMLInputElement>(null);
  // #65 - the "Import from file" button opens straight into the CSV picker.
  useEffect(() => {
    if (autoImport) fileRef.current?.click();
  }, [autoImport]);

  const scopedCats = () => (scope === 'all' || kind === 'backup' ? CATS.slice() : [category]);
  const fetchContacts = async (cat: string): Promise<ContactRow[]> => {
    const r = await api.get<{ items: ContactRow[] }>(`/contacts?category=${cat}`);
    return Array.isArray(r?.items) ? r.items : [];
  };

  const download = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      if (kind === 'contacts') {
        const cats = scopedCats();
        const all = (await Promise.all(cats.map(fetchContacts))).flat();
        saveBlob(`contacts-${stamp()}.csv`, 'text/csv', contactsCsv(all));
      } else if (kind === 'activity') {
        // #61 shaped /audit as { rows, total } with actor/action/entity/ts fields.
        const resp = await api
          .get<{ rows: { actor: string; action: string; entity: string; ts: string }[] }>('/audit')
          .catch(() => ({ rows: [] }));
        const rows = resp?.rows ?? [];
        const csv = toCsv(
          ['Actor', 'Action', 'Entity', 'When'],
          rows.map((r) => [r.actor, r.action, r.entity, r.ts]),
        );
        saveBlob(`activity-${stamp()}.csv`, 'text/csv', csv);
      } else if (kind === 'followups') {
        const rows = await api
          .get<{ contactId: string; dueAt: string; note?: string | null }[]>('/followups')
          .catch(() => []);
        const sorted = (rows ?? []).slice().sort((a, b) => (a.dueAt < b.dueAt ? -1 : 1));
        const csv = toCsv(
          ['Contact ID', 'Due', 'Note'],
          sorted.map((r) => [r.contactId, r.dueAt, r.note ?? '']),
        );
        saveBlob(`followups-${stamp()}.csv`, 'text/csv', csv);
      } else if (kind === 'category') {
        const contacts = await fetchContacts(category);
        saveBlob(
          `${category}-${stamp()}.json`,
          'application/json',
          JSON.stringify({ category, exportedAt: new Date().toISOString(), contacts }, null, 2),
        );
      } else {
        // full backup
        const byCat: Record<string, ContactRow[]> = {};
        for (const c of CATS) byCat[c] = await fetchContacts(c);
        saveBlob(
          `backup-${stamp()}.json`,
          'application/json',
          JSON.stringify({ exportedAt: new Date().toISOString(), contacts: byCat }, null, 2),
        );
      }
      setMsg('Download started.');
    } catch {
      setMsg('Could not generate the export.');
    } finally {
      setBusy(false);
    }
  };

  const onImportFile = async (file: File) => {
    setBusy(true);
    setMsg(null);
    try {
      const text = await file.text();
      if (looksBinary(text)) {
        setMsg(
          'That file is not a CSV (it looks like an Excel workbook). In Excel, use File → Save As → CSV UTF-8, then import the .csv file.',
        );
        return;
      }
      const rows = parseCsv(text);
      if (rows.length < 2) {
        setMsg('That CSV has no data rows.');
        return;
      }
      const stats: BuildStats = { apptPast: 0, apptUnreadable: 0 };
      const built = buildRecords(rows, category, stats);
      if (built.length === 0) {
        setMsg('No rows had both a first and last name — check the column headers.');
        return;
      }
      // ehr#31: rows the API would refuse are skipped and named, the rest still import.
      const { ok: records, bad } = splitValid(built);
      const badNote = bad.length
        ? ` Skipped ${bad.length} row${bad.length === 1 ? '' : 's'}: ${bad.slice(0, 3).join('; ')}${bad.length > 3 ? `; and ${bad.length - 3} more` : ''}.`
        : '';
      if (records.length === 0) {
        setMsg(`Nothing to import.${badNote}`);
        return;
      }
      // Send in chunks (the API caps each request; JSON bodies are capped at 6 MB)
      // and accumulate the per-chunk results.
      const total: ImportResult = {
        dryRun: preview,
        created: 0,
        updated: 0,
        skipped: 0,
        notesImported: 0,
        notesSkipped: 0,
        errors: [],
        warnings: [],
        sameName: [],
        appointmentsCreated: 0,
        appointmentsSkipped: 0,
      };
      for (const batch of chunk(records, IMPORT_MAX_CONTACTS)) {
        const res = await api.post<ImportResult>('/import/contacts', {
          dryRun: preview,
          contacts: batch,
        });
        total.created += res.created;
        total.updated += res.updated;
        total.skipped += res.skipped;
        total.notesImported += res.notesImported;
        total.notesSkipped! += res.notesSkipped ?? 0;
        total.errors.push(...res.errors);
        total.warnings!.push(...(res.warnings ?? []));
        total.sameName!.push(...(res.sameName ?? []));
        total.appointmentsCreated! += res.appointmentsCreated ?? 0;
        total.appointmentsSkipped! += res.appointmentsSkipped ?? 0;
      }
      // #31: past appointments are skipped in the browser; already-booked ones by the API.
      const apptSkipped = total.appointmentsSkipped! + stats.apptPast;
      const warns = total.warnings!;
      const same = total.sameName!;
      const verb = preview ? 'Would import' : 'Imported';
      const parts = [
        `${verb} ${total.created} new`,
        `${total.updated} updated`,
        total.notesImported
          ? `${total.notesImported} note${total.notesImported === 1 ? '' : 's'}`
          : '',
        // ehr#31: a re-import skips the notes a client already has
        total.notesSkipped
          ? `${total.notesSkipped} note${total.notesSkipped === 1 ? '' : 's'} already there`
          : '',
        total.errors.length
          ? `${total.errors.length} row${total.errors.length === 1 ? '' : 's'} with errors (e.g. ${total.errors[0]!.name}: ${total.errors[0]!.message})`
          : '',
        total.appointmentsCreated
          ? `${total.appointmentsCreated} appointment${total.appointmentsCreated === 1 ? '' : 's'}${preview ? ' to schedule' : ' scheduled'}`
          : '',
        apptSkipped
          ? `${apptSkipped} appointment${apptSkipped === 1 ? '' : 's'} skipped (past or already booked)`
          : '',
        stats.apptUnreadable
          ? `${stats.apptUnreadable} appointment${stats.apptUnreadable === 1 ? '' : 's'} with an unreadable date/time`
          : '',
        // ehr#265: new records for names already here (no Client ID, MRN or email to match).
        same.length
          ? `${same.length} ${same.length === 1 ? 'is a second record' : 'are second records'} for a name already in Credify (${same
              .slice(0, 3)
              .map((w) => w.name)
              .join(
                ', ',
              )}${same.length > 3 ? ', …' : ''}): add a Client ID, MRN or email column to match ${same.length === 1 ? 'it' : 'them'}`
          : '',
        // #31: e.g. a Status label the org doesn't have — the row imports without it.
        warns.length
          ? `${warns.length} warning${warns.length === 1 ? '' : 's'} (e.g. ${warns[0]!.message})`
          : '',
      ].filter(Boolean);
      setMsg(
        (preview ? 'Preview — nothing saved. ' : '') +
          parts.join(', ') +
          (preview ? '. Uncheck "Preview only" and re-import to save.' : '.') +
          badNote,
      );
    } catch (e) {
      setMsg(e instanceof Error ? `Import failed: ${e.message}` : 'Could not read that file.');
    } finally {
      setBusy(false);
    }
  };

  const body = (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="ie-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Import / Export Data"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ie-head">
          <div>
            <h2>Import / Export Data</h2>
            <p className="muted">
              Bring contacts in from a spreadsheet, or download contacts, activity, and follow-ups —
              CSV for spreadsheets, JSON for backups.
            </p>
          </div>
          <div className="ie-head-actions">
            <label
              className="ie-preview-toggle"
              title="Validate and report what would import, without saving"
            >
              <input
                type="checkbox"
                checked={preview}
                onChange={(e) => setPreview(e.target.checked)}
                disabled={busy}
              />
              Preview only
            </label>
            <button
              className="btn"
              title="A CSV with every column the importer reads, plus one example row"
              onClick={() =>
                saveBlob(
                  'credify-import-template.csv',
                  'text/csv',
                  toCsv(TEMPLATE_HEADERS, [TEMPLATE_EXAMPLE]),
                )
              }
              disabled={busy}
            >
              ⬇ Template
            </button>
            <button className="btn" onClick={() => fileRef.current?.click()} disabled={busy}>
              ⬆ Import CSV
            </button>
            <button className="ie-close" aria-label="Close" onClick={onClose}>
              ✕
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImportFile(f);
              e.target.value = '';
            }}
          />
        </div>

        <div className="ie-cards">
          {CARDS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`ie-card ${kind === c.key ? 'selected' : ''}`}
              onClick={() => setKind(c.key)}
            >
              <span className={`ie-badge ${c.fmt === 'JSON' ? 'json' : ''}`}>{c.fmt}</span>
              {kind === c.key && <span className="ie-check">✓</span>}
              <span className="ie-card-title">{c.title}</span>
              <span className="ie-card-desc">{c.desc}</span>
            </button>
          ))}
        </div>

        <div className="ie-scope">
          <div className="ie-scope-head">
            <span className="ie-scope-label">Scope</span>
            <span className="muted">Which contacts to include</span>
          </div>
          <div className="ie-scope-btns">
            <button
              type="button"
              className={`ie-scope-btn ${scope === 'category' ? 'active' : ''}`}
              disabled={kind === 'backup'}
              onClick={() => setScope('category')}
            >
              {catPlural(category)} only
            </button>
            <button
              type="button"
              className={`ie-scope-btn ${scope === 'all' || kind === 'backup' ? 'active' : ''}`}
              onClick={() => setScope('all')}
            >
              All categories
            </button>
          </div>
        </div>

        <div className="ie-foot">
          {msg && <span className="ie-msg">{msg}</span>}
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn primary" onClick={download} disabled={busy}>
            {busy ? 'Working…' : '⬇ Download'}
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(body, document.body);
}
